import axios, { AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import type {
  ApiResponse,
  PaginatedData,
  Customer,
  Order,
  OrderDetail,
  Product,
  Material,
  BOM,
  Wage,
  User,
  ProductionOrder,
  ProductionDashboard,
  ProductionBoardOverview,
  DashboardStats,
  WorkReport,
  DeliveryArrangement,
} from '@/types';

const configuredApiBaseUrl = import.meta.env.VITE_API_URL?.trim();

export const API_BASE_URL = configuredApiBaseUrl || '/api';
export const BACKEND_BASE_URL = configuredApiBaseUrl
  ? configuredApiBaseUrl.replace(/\/api\/?$/, '')
  : window.location.origin;

export const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor unwraps to the server's ApiResponse body.
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// -----------------------------------------------------------------------------
// Typed verb helpers
//
// The response interceptor above returns `response.data` (the server's
// { success, message, data } envelope). So the real runtime return type of
// `api.get(...)` is ApiResponse<T>, but axios's own type signature still says
// AxiosResponse<any>, which makes every caller fall back to `as any`.
//
// These thin wrappers fix the lie: they keep the exact same runtime behavior
// and cast the return to Promise<ApiResponse<T>>. Call sites get inference:
// `(await orderApi.getOrders()).data.list` is now `Order[]`, no cast needed.
// -----------------------------------------------------------------------------

function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return api.get(url, config) as unknown as Promise<ApiResponse<T>>;
}
function apiPost<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return api.post(url, data, config) as unknown as Promise<ApiResponse<T>>;
}
function apiPut<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return api.put(url, data, config) as unknown as Promise<ApiResponse<T>>;
}
function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return api.delete(url, config) as unknown as Promise<ApiResponse<T>>;
}
function apiPatch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return api.patch(url, data, config) as unknown as Promise<ApiResponse<T>>;
}

// ID and simple ack shapes used by many mutation endpoints.
type IdAck = { id: number };
type Ack = Record<string, never>;

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    apiPost<{ token: string; user: User }>('/auth/login', { username, password }),
  registerInternal: (data: {
    username: string;
    password: string;
    name: string;
    phone?: string;
    email?: string;
    role_profile: string;
    primary_factory_id: number;
  }) => apiPost<Record<string, never>>('/auth/register/internal', data),
  directClientLogin: (data: {
    customer_name: string;
    company_name?: string;
    phone: string;
    email?: string;
  }) => apiPost<{ token: string; user: User }>('/auth/client/direct-login', data),
  me: () => apiGet<User & { permissions: string[] }>('/auth/me'),
  changePassword: (oldPassword: string, newPassword: string) =>
    apiPost<Ack>('/auth/change-password', { oldPassword, newPassword }),
};

// User API
export const userApi = {
  getUsers: () => apiGet<User[]>('/users'),
  getUser: (id: number) => apiGet<User>(`/users/${id}`),
  createUser: (data: any) => apiPost<IdAck>('/users', data),
  updateUser: (id: number, data: any) => apiPut<Ack>(`/users/${id}`, data),
  deleteUser: (id: number) => apiDelete<Ack>(`/users/${id}`),
  resetPassword: (id: number, password: string) =>
    apiPost<Ack>(`/users/${id}/reset-password`, { password }),
};

// Customer API
export const customerApi = {
  getCustomers: (params?: any) => apiGet<PaginatedData<Customer>>('/customers', { params }),
  getSeaCustomers: () => apiGet<Customer[]>('/customers/sea'),
  getCustomer: (id: number) => apiGet<Customer>(`/customers/${id}`),
  createCustomer: (data: any) => apiPost<IdAck>('/customers', data),
  updateCustomer: (id: number, data: any) => apiPut<Ack>(`/customers/${id}`, data),
  deleteCustomer: (id: number) => apiDelete<Ack>(`/customers/${id}`),
  checkPhone: (phone: string, exclude_id?: number) =>
    apiPost<{ duplicate: boolean; customer_id?: number; customer_name?: string; owner?: { id: number; name: string } | null }>(
      '/customers/check-phone',
      { phone, exclude_id },
    ),
  claimCustomer: (id: number) => apiPost<Ack>(`/customers/${id}/claim`, {}),
  transferCustomer: (id: number, owner_id: number | null) =>
    apiPost<Ack>(`/customers/${id}/transfer`, { owner_id }),
};

// Follow-up API
export const followUpApi = {
  list: (customer_id: number) => apiGet<any[]>('/follow-ups', { params: { customer_id } }),
  create: (data: { customer_id: number; content?: string; next_follow_up_date?: string }) =>
    apiPost<IdAck>('/follow-ups', data),
  remove: (id: number) => apiDelete<Ack>(`/follow-ups/${id}`),
};

// Commissions
export const commissionApi = {
  mine: (status?: string) => apiGet<{ list: any[]; summary: any }>('/commissions/mine', { params: { status } }),
  list: (params?: any) => apiGet<any[]>('/commissions', { params }),
  settle: (id: number, data?: any) => apiPost<{ final_amount?: number }>(`/commissions/${id}/settle`, data || {}),
  trigger: (order_id: number) => apiPost<any>(`/commissions/trigger/${order_id}`, {}),
  scanOrders: () => apiPost<{ scanned: number; generated: number }>('/commissions/scan-orders', {}),
  defaults: () => apiGet<any>('/commissions/defaults'),
  rules: () => apiGet<any[]>('/commissions/rules'),
  createRule: (data: { customer_type?: string; product_category?: string; rate: number; effective_from?: string }) =>
    apiPost<IdAck>('/commissions/rules', data),
  updateRule: (id: number, data: { customer_type?: string; product_category?: string; rate: number; effective_from?: string }) =>
    apiPut<Ack>(`/commissions/rules/${id}`, data),
  deleteRule: (id: number) => apiDelete<Ack>(`/commissions/rules/${id}`),
  adjust: (id: number, data: { adjustment_items?: any[]; manual_amount?: number | string | null; manual_note?: string }) =>
    apiPut<Ack>(`/commissions/${id}/adjust`, data),
};

// Order change requests
export const changeRequestApi = {
  list: (params?: any) => apiGet<any[]>('/change-requests', { params }),
  create: (data: { order_id: number; changes: Record<string, any>; reason?: string }) =>
    apiPost<IdAck>('/change-requests', data),
  approve: (id: number, review_note?: string) =>
    apiPost<Ack>(`/change-requests/${id}/approve`, { review_note }),
  reject: (id: number, review_note?: string) =>
    apiPost<Ack>(`/change-requests/${id}/reject`, { review_note }),
};

// Notifications
export const notificationApi = {
  list: (unread_only?: boolean) =>
    apiGet<{ list: any[]; unread: number }>('/notifications', { params: { unread_only } }),
  markRead: (id: number) => apiPost<Ack>(`/notifications/${id}/read`, {}),
  markAllRead: () => apiPost<Ack>('/notifications/read-all', {}),
  remove: (id: number) => apiDelete<Ack>(`/notifications/${id}`),
};

// Business workshop home aggregate + performance
export const businessHomeApi = {
  getHome: () => apiGet<any>('/business/home'),
  performance: (months?: number) => apiGet<any>('/business/performance', { params: { months } }),
};

// Business order wizard + management
export const businessOrderApi = {
  list: (params?: any) => apiGet<PaginatedData<Order>>('/business-orders', { params }),
  get: (id: number) => apiGet<OrderDetail>(`/business-orders/${id}`),
  create: (data: any) => apiPost<{ id: number; order_no: string; quote_amount: number }>('/business-orders', data),
  update: (id: number, data: any) => apiPut<{ id: number; order_no: string; quote_amount: number }>(`/business-orders/${id}`, data),
  delete: (id: number) => apiDelete<Ack>(`/business-orders/${id}`),
  getItems: (id: number) => apiGet<any[]>(`/business-orders/${id}/items`),
  getAuxiliaryMaterialOptions: (params?: any) => apiGet<Material[]>('/business-orders/auxiliary-material-options', { params }),
  stockInAuxiliaryMaterial: (data: any) => apiPost<Material>('/business-orders/auxiliary-materials/stock-in', data),
  updateBusinessStatus: (id: number, data: { business_status?: string; payment_status?: string; note?: string }) =>
    apiPost<Ack>(`/business-orders/${id}/business-status`, data),
  supplementPackaging: (id: number) =>
    apiPost<{ insertedCount: number; status: string }>(`/business-orders/${id}/supplement-packaging`, {}),
  quotationPdfUrl: (id: number) => `${API_BASE_URL}/business-orders/${id}/quotation.pdf`,
  fumigationPdfUrl: (id: number) => `${API_BASE_URL}/business-orders/${id}/fumigation.pdf`,
  orderPrintXlsxUrl: (id: number) => `${API_BASE_URL}/business-orders/${id}/order-print.xlsx`,
  orderPrintPdfUrl: (id: number) => `${API_BASE_URL}/business-orders/${id}/order-print.xlsx`,
  convertToOrder: (id: number) => apiPost<Ack>(`/business-orders/${id}/convert-to-order`, {}),
  uploadVoucher: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiPost<{ filename: string; url: string; size: number }>('/business-orders/upload-voucher', fd);
  },
};

// Product API
export const productApi = {
  getProducts: (params?: any) => apiGet<PaginatedData<Product>>('/products', { params }),
  getProduct: (id: number) => apiGet<Product>(`/products/${id}`),
  createProduct: (data: any) => apiPost<IdAck>('/products', data),
  updateProduct: (id: number, data: any) => apiPut<Ack>(`/products/${id}`, data),
  deleteProduct: (id: number) => apiDelete<Ack>(`/products/${id}`),
  getCategories: () => apiGet<any[]>('/products/categories/list'),
};

// Order API
export const orderApi = {
  getOrders: (params?: any) => apiGet<PaginatedData<Order>>('/orders', { params }),
  getOrder: (id: number) => apiGet<OrderDetail>(`/orders/${id}`),
  createOrder: (data: any) => apiPost<IdAck>('/orders', data),
  updateOrder: (id: number, data: any) => apiPut<Ack>(`/orders/${id}`, data),
  deleteOrder: (id: number) => apiDelete<Ack>(`/orders/${id}`),
  updateStatus: (id: number, status: string, _operator_id?: number, remark?: string) =>
    apiPost<Ack>(`/orders/${id}/status`, { status, remark }),
  uploadDrawing: (id: number, data: any) => apiPost<Ack>(`/orders/${id}/drawing`, data),
  approveDrawing: (id: number, _operator_id?: number, remark?: string) =>
    apiPost<Ack>(`/orders/${id}/approve`, { remark }),
  getQRCode: (id: number) => apiGet<{ qr_code: string }>(`/orders/${id}/qrcode`),
  orderPrintXlsxUrl: (id: number) => `${API_BASE_URL}/orders/${id}/order-print.xlsx`,
  orderPrintPdfUrl: (id: number) => `${API_BASE_URL}/orders/${id}/order-print.xlsx`,
  /**
   * Re-route this order's production to another factory. Allowed for admin
   * + production roles only. Server also rewrites factory_id on attached
   * production_orders / work_reports / delivery / order_designs so the
   * destination factory's lists pick the order up immediately.
   */
  dispatchFactory: (id: number, factory_id: number, reason?: string) =>
    apiPost<{ order_id: number; producing_factory_id: number; factory_name: string }>(
      `/orders/${id}/dispatch-factory`,
      { factory_id, reason },
    ),
  dispatchHistory: (id: number) =>
    apiGet<
      Array<{
        id: number;
        from_factory_id: number;
        to_factory_id: number;
        from_factory_name: string | null;
        to_factory_name: string | null;
        reason: string | null;
        operator_id: number | null;
        operator_name: string | null;
        created_at: string;
      }>
    >(`/orders/${id}/dispatch-history`),
};

// Production API
export const productionApi = {
  getProductionOrders: (params?: any) => apiGet<PaginatedData<ProductionOrder>>('/production/orders', { params }),
  getProductionOrder: (id: number) => apiGet<ProductionOrder>(`/production/orders/${id}`),
  createProductionOrder: (data: any) => apiPost<IdAck>('/production/orders', data),
  assignOrder: (id: number, data: any) => apiPost<Ack>(`/production/orders/${id}/assign`, data),
  updateStatus: (id: number, status: string, _operator_id?: number) =>
    apiPost<Ack>(`/production/orders/${id}/status`, { status }),
  reportWork: (id: number, data: any) => apiPost<Ack>(`/production/orders/${id}/report`, data),
  getWorkReports: (params?: any) => apiGet<PaginatedData<WorkReport>>('/production/reports', { params }),
  getDashboard: () => apiGet<ProductionDashboard>('/production/dashboard'),
  getBoardOverview: (params?: any) => apiGet<ProductionBoardOverview>('/production/board/overview', { params }),
};

// Material API
export const materialApi = {
  getMaterials: (params?: any) => apiGet<PaginatedData<Material>>('/materials', { params }),
  getMaterial: (id: number) => apiGet<Material>(`/materials/${id}`),
  createMaterial: (data: any) => apiPost<IdAck>('/materials', data),
  stockInMaterial: (data: any) => apiPost<Material>('/materials/stock-in', data),
  updateMaterial: (id: number, data: any) => apiPut<Ack>(`/materials/${id}`, data),
  // 备注专用：只改 materials.remark，不动其他字段。空串 / null → 清空。
  updateMaterialRemark: (id: number, remark: string | null) =>
    apiPatch<{ remark: string | null }>(`/materials/${id}/remark`, { remark }),
  deleteMaterial: (id: number) => apiDelete<Ack>(`/materials/${id}`),
  bulkDeleteMaterials: (ids: number[]) =>
    apiPost<{ deleted: number; blocked: { id: number; reason: string }[] }>(
      '/materials/bulk-delete',
      { ids },
    ),
  bulkUpdateMaterials: (ids: number[], patch: Record<string, any>) =>
    apiPost<{ updated: number; blocked: { id: number; reason: string }[] }>(
      '/materials/bulk-update',
      { ids, patch },
    ),
  getCategories: () => apiGet<any[]>('/materials/meta/categories'),
};

// Material bulk-import (Excel preview + commit)
export interface MaterialImportPreviewSheet {
  name: string;
  headers: string[];
  sample: string[][];
  totalDataRows: number;
}
export interface MaterialImportPreviewData {
  previewToken: string;
  fileName: string;
  ttlSeconds: number;
  availableFields: readonly string[];
  sheets: MaterialImportPreviewSheet[];
}
export interface MaterialImportSheetMapping {
  sheetName: string;
  columnMap: Record<string, number | undefined>;
  defaultCategory?: string;
  defaultUnit?: string;
}
export interface MaterialImportConflict {
  sheetName: string;
  rowIndex: number;
  name: string;
  specification: string;
  reason:
    | 'duplicate_in_factory'
    | 'duplicate_within_import'
    | 'missing_unit'
    | 'duplicate_material_no'
    | 'duplicate_material_no_within_import';
}
export interface MaterialImportCommitResult {
  batchId: number;
  successCount: number;
  skippedCount: number;
  conflictCount: number;
  conflicts: MaterialImportConflict[];
}
export interface MaterialImportBatch {
  id: number;
  factory_id: number;
  operator_id: number | null;
  file_name: string | null;
  success_count: number;
  skipped_count: number;
  conflict_count: number;
  created_at: string;
}

export const materialImportApi = {
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiPost<MaterialImportPreviewData>('/materials/import/preview', fd);
  },
  commit: (body: {
    previewToken: string;
    factoryId: number;
    sheetMappings: MaterialImportSheetMapping[];
  }) => apiPost<MaterialImportCommitResult>('/materials/import/commit', body),
  batches: () => apiGet<MaterialImportBatch[]>('/materials/import/batches'),
};

// BOM accountant audit — list/detail/decide.
// Mirrors backend/src/routes/bom-audits.ts contract.
export type BomAuditStatus = 'pending' | 'approved' | 'rejected' | 'flagged';
export type BomAuditDecision = 'approve' | 'reject' | 'flag';
export type BomAuditGateStage = 'completed' | 'confirmed' | 'disabled';

export interface BomAuditListRow {
  id: number;
  order_id: number;
  factory_id: number;
  status: BomAuditStatus;
  reviewer_id: number | null;
  reviewed_at: string | null;
  remark: string | null;
  created_at: string;
  updated_at: string;
  order_no: string | null;
  project_no?: string | null;
  customer_name: string | null;
  reviewer_name: string | null;
  actual_cost: number | null;
  theoretical_cost: number | null;
  missing_count: number | null;
  unexpected_count: number | null;
}

export interface BomAuditSnapshotItem {
  material_id: number;
  material_no: string;
  name: string;
  unit: string;
  specification: string | null;
  required_quantity: number;
  consumed_quantity: number;
  shortage_quantity: number;
  unit_price: number | null;
  theoretical_cost: number;
}

export interface BomAuditLedgerItem {
  material_id: number;
  warehouse_id: number;
  quantity: number;
  unit_price: number | null;
  total_amount: number;
  factory_id: number;
  created_at: string;
}

export interface BomAuditVarianceRow {
  material_id: number;
  material_no: string;
  name: string;
  unit: string;
  required_qty: number;
  actual_qty: number;
  qty_variance: number;
  theoretical_cost: number;
  actual_cost: number;
  cost_variance: number;
}

export interface BomAuditVariance {
  rows: BomAuditVarianceRow[];
  total_required_qty: number;
  total_actual_qty: number;
  total_theoretical_cost: number;
  total_actual_cost: number;
  missing_consumption_count: number;
  unexpected_consumption_count: number;
}

export interface BomAuditDetail {
  id: number;
  order_id: number;
  order_no: string | null;
  project_no?: string | null;
  customer_name: string | null;
  order_status: string;
  factory_id: number;
  status: BomAuditStatus;
  reviewer_id: number | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  remark: string | null;
  created_at: string;
  updated_at: string;
  bom_snapshot: BomAuditSnapshotItem[];
  actual_consumption: BomAuditLedgerItem[];
  variance_summary: BomAuditVariance;
}

export const bomAuditApi = {
  list: (params?: { status?: BomAuditStatus; order_id?: number; page?: number; limit?: number }) =>
    apiGet<PaginatedData<BomAuditListRow> & { gate_stage?: BomAuditGateStage }>('/bom-audits', { params }),
  detail: (id: number) =>
    apiGet<BomAuditDetail & { gate_stage?: BomAuditGateStage }>(`/bom-audits/${id}`),
  approve: (id: number, remark?: string) =>
    apiPost<{ id: number; status: BomAuditStatus }>(`/bom-audits/${id}/approve`, { remark }),
  reject: (id: number, remark: string) =>
    apiPost<{ id: number; status: BomAuditStatus }>(`/bom-audits/${id}/reject`, { remark }),
  flag: (id: number, remark?: string) =>
    apiPost<{ id: number; status: BomAuditStatus }>(`/bom-audits/${id}/flag`, { remark }),
};

// Inventory API
//
// All read endpoints accept an optional `factory_id` query parameter that the
// inventory page's segmented control passes through to switch which factory's
// stock the user is viewing without re-issuing the JWT. `factory_id=0` means
// "全分厂汇总" (requires hasAllFactoriesAccess).
export const inventoryApi = {
  getInventory: (params?: any) => apiGet<PaginatedData<Material>>('/inventory', { params }),
  getLedger: (params?: any) => apiGet<PaginatedData<any>>('/inventory/ledger', { params }),
  getWarehouses: (params?: { factory_id?: number }) =>
    apiGet<Warehouse[]>('/inventory/warehouses', { params }),
  // 勾选式批量入库/出库 — 仓库一致、物料各自数量；后端按 factory 校验，跨厂物料进 blocked。
  // 单行入库/出库 (POST /inventory/in /out) 仍保留在后端供 smoke test、
  // 程序化调用使用，但前端 UI 已统一走 bulk 路径（勾 1 行 = 单行操作）。
  bulkStockIn: (data: {
    warehouse_id: number;
    remark?: string;
    items: Array<{ material_id: number; quantity: number }>;
  }) =>
    apiPost<{
      processed: Array<{ material_id: number; new_balance: number }>;
      blocked: Array<{ material_id: number; reason: string }>;
    }>('/inventory/bulk-in', data),
  bulkStockOut: (data: {
    warehouse_id: number;
    remark?: string;
    items: Array<{ material_id: number; quantity: number }>;
  }) =>
    apiPost<{
      processed: Array<{ material_id: number; new_balance: number }>;
      blocked: Array<{ material_id: number; reason: string }>;
    }>('/inventory/bulk-out', data),
  transfer: (data: any) => apiPost<Ack>('/inventory/transfer', data),
  adjust: (data: any) => apiPost<Ack>('/inventory/adjust', data),
  getSummary: (params?: { factory_id?: number }) =>
    apiGet<any>('/inventory/summary', { params }),
};

export interface Warehouse {
  id: number;
  code: string;
  name: string;
  location?: string | null;
  factory_id: number;
  status: number;
}

// BOM API
export const bomApi = {
  getBOMs: (params?: any) => apiGet<PaginatedData<BOM>>('/boms', { params }),
  getBOM: (id: number) => apiGet<BOM & { items: any[] }>(`/boms/${id}`),
  createBOM: (data: any) => apiPost<IdAck>('/boms', data),
  updateBOM: (id: number, data: any) => apiPut<Ack>(`/boms/${id}`, data),
  deleteBOM: (id: number) => apiDelete<Ack>(`/boms/${id}`),
  approveBOM: (id: number, approved_by: number) =>
    apiPost<Ack>(`/boms/${id}/approve`, { approved_by }),
  calculate: (id: number, dimensions: any, quantity: number) =>
    apiPost<any>(`/boms/${id}/calculate`, { dimensions, quantity }),
};

// Wage API
export const wageApi = {
  getWages: (params?: any) => apiGet<Wage[]>('/wages', { params }),
  getStandards: (params?: any) => apiGet<any[]>('/wages/standards', { params }),
  setStandard: (data: any) => apiPost<IdAck>('/wages/standards', data),
  calculate: (year_month: string) => apiPost<any>('/wages/calculate', { year_month }),
  confirm: (id: number, data: any) => apiPost<Ack>(`/wages/${id}/confirm`, data),
  pay: (id: number) => apiPost<Ack>(`/wages/${id}/pay`, {}),
  getSummary: (year_month: string) => apiGet<any>('/wages/summary/overview', { params: { year_month } }),
  getFinanceSummary: (year_month?: string) => apiGet<any>('/wages/analytics/finance-summary', { params: { year_month } }),
  downloadErpData: (format: 'json' | 'csv' = 'json', year_month?: string) =>
    api.get('/wages/analytics/erp-export', {
      params: { format, year_month },
      responseType: 'blob',
    }) as unknown as Promise<Blob>,
};

// Dashboard API
export const dashboardApi = {
  getStats: () => apiGet<DashboardStats>('/dashboard/stats'),
  getOrderTrend: (months?: number) => apiGet<any[]>('/dashboard/order-trend', { params: { months } }),
  getProductionTrend: (days?: number) => apiGet<any[]>('/dashboard/production-trend', { params: { days } }),
  getPendingTasks: () => apiGet<any>('/dashboard/pending-tasks'),
  getActivities: () => apiGet<{ recent_orders?: any[]; recent_reports?: any[] }>('/dashboard/activities'),
  getTopBusiness: (limit?: number) =>
    apiGet<Array<{ business_id: number; name: string; total: number }>>('/dashboard/top-business', {
      params: { limit },
    }),
  /**
   * Per-factory KPI breakdown. Only available in sentinel mode (admin
   * currently_factory_id = 0). Drilled-in admins see their own factory's
   * slice via the normal getStats() — calling this from a single-factory
   * context will return 403.
   */
  getByFactory: () =>
    apiGet<
      Array<{
        factory_id: number;
        code: string;
        name: string;
        short_name: string | null;
        total_orders: number;
        today_orders: number;
        in_production: number;
        completed: number;
        total_revenue: number;
        month_revenue: number;
        total_work_orders: number;
        in_progress_work_orders: number;
        total_completed_qty: number;
        inventory_value: number;
      }>
    >('/dashboard/by-factory'),
};

export const integrationApi = {
  testGjpConnection: (data: any) => apiPost<any>('/integration/gjp/test-connection', data),
  syncGjpData: (data: any) => apiPost<any>('/integration/gjp/sync', data),
  getGjpStatus: () => apiGet<any>('/integration/gjp/status'),
  getGjpAuthUrl: () => apiGet<{ authUrl: string; state: string; redirectUri: string }>('/integration/gjp/oauth/url'),
  refreshGjpToken: () => apiPost<any>('/integration/gjp/refresh-token', {}),
  getGjpSnapshot: () => apiGet<any>('/integration/gjp/snapshot'),
  getGjpBasicData: (params: { method: string; pageIndex?: number; pageSize?: number }) => apiGet<any>('/integration/gjp/basic-data', { params }),
  testDingTalkConnection: (data?: any) => apiPost<any>('/integration/dingtalk/test-connection', data || {}),
  syncDingTalkData: () => apiPost<any>('/integration/dingtalk/sync', {}),
  getDingTalkSnapshot: () => apiGet<any>('/integration/dingtalk/snapshot'),
  getDeepSeekStatus: () => apiGet<{ configured: boolean; apiKeyPreview?: string; baseUrl: string; model: string }>('/integration/deepseek/status'),
  testDeepSeekConnection: (data: any) => apiPost<any>('/integration/deepseek/test-connection', data),
  chatDeepSeek: (data: any) => apiPost<any>('/integration/deepseek/chat', data),
  publicDeepSeekChat: (data: any) => apiPost<any>('/integration/deepseek/public-chat', data),
};

export const designApi = {
  getDesignOrders: () => apiGet<any[]>('/design/orders'),
  getDesignOrder: (id: number) => apiGet<any>(`/design/orders/${id}`),
  generatePackage: (id: number, data: any) => apiPost<any>(`/design/orders/${id}/generate`, data),
  uploadAttachments: (id: number, data: FormData) => apiPost<any>(`/design/orders/${id}/attachments`, data),
  reviewPackage: (id: number, data: any) => apiPost<Ack>(`/design/orders/${id}/review`, data),
  exportPackage: (id: number) => apiGet<any>(`/design/orders/${id}/export-package`),
  renderVolcengineImage: (data: any) => apiPost<any>('/design/volcengine-render', data),
};

export const deliveryApi = {
  listOrders: (params?: any) => apiGet<PaginatedData<any>>('/delivery/orders', { params }),
  getOrder: (id: number) => apiGet<any>(`/delivery/orders/${id}`),
  saveArrangement: (id: number, data: Partial<DeliveryArrangement> & { tracking_events?: any[] }) =>
    apiPut<Ack>(`/delivery/orders/${id}/arrangement`, data),
  uploadProof: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiPost<any>('/delivery/upload-proof', fd);
  },
};

export const settingsApi = {
  getOverview: () => apiGet<any>('/settings/overview'),
  getCompanyProfile: () => apiGet<any>('/settings/company'),
  saveCompanyProfile: (data: any) => apiPut<Ack>('/settings/company', data),
  getSiteSettings: () => apiGet<any>('/settings/site'),
  saveSiteSettings: (data: any) => apiPut<Ack>('/settings/site', data),
  getMediaLibrary: () => apiGet<any[]>('/settings/media'),
  getBusinessAuxiliaryUi: () => apiGet<any[]>('/settings/business-auxiliary-ui'),
  saveBusinessAuxiliaryUi: (data: any[]) => apiPut<Ack>('/settings/business-auxiliary-ui', data),
  getBusinessProductCatalog: () => apiGet<any>('/settings/business-product-catalog'),
  saveBusinessProductCatalog: (data: any) => apiPut<Ack>('/settings/business-product-catalog', data),
  getModuleSettings: () => apiGet<any[]>('/settings/modules'),
  saveModuleSettings: (data: any[]) => apiPut<Ack>('/settings/modules', data),
  getPrintProfile: () => apiGet<any[]>('/settings/print-profile'),
  savePrintProfile: (data: any[]) => apiPut<Ack>('/settings/print-profile', data),
  getRolePermissions: () => apiGet<any>('/settings/role-permissions'),
  saveRolePermissions: (data: any) => apiPut<Ack>('/settings/role-permissions', data),
  getScreenSettings: () => apiGet<any>('/settings/screen'),
  saveScreenSettings: (data: any) => apiPut<Ack>('/settings/screen', data),
  getAbout: () => apiGet<any>('/settings/about'),
  uploadSiteAsset: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiPost<any>('/settings/media/upload', fd);
  },
  deleteSiteAsset: (id: string) => apiDelete<Ack>(`/settings/media/${id}`),
  getTemplates: () => apiGet<any>('/settings/templates'),
  createProcessTemplate: (data: any) => apiPost<IdAck>('/settings/templates/processes', data),
  updateProcessTemplate: (id: number, data: any) => apiPut<Ack>(`/settings/templates/processes/${id}`, data),
  deleteProcessTemplate: (id: number) => apiDelete<Ack>(`/settings/templates/processes/${id}`),
  createBomTemplate: (data: any) => apiPost<IdAck>('/settings/templates/boms', data),
  updateBomTemplate: (id: number, data: any) => apiPut<Ack>(`/settings/templates/boms/${id}`, data),
  deleteBomTemplate: (id: number) => apiDelete<Ack>(`/settings/templates/boms/${id}`),
  getDingTalkConfig: () => apiGet<any>('/settings/oa/dingtalk'),
  saveDingTalkConfig: (data: any) => apiPut<Ack>('/settings/oa/dingtalk', data),
  getDingTalkStatus: () => apiGet<{ configured: boolean; message: string }>('/settings/oa/dingtalk/status'),
  getNgpConfig: () => apiGet<any>('/settings/oa/ngp'),
  saveNgpConfig: (data: any) => apiPut<Ack>('/settings/oa/ngp', data),
  getNgpStatus: () => apiGet<any>('/settings/oa/ngp/status'),
};

// Multi-factory: list user's accessible factories + switch current factory.
// Switching mints a fresh JWT with the new current_factory_id baked into the
// claims; callers must replace authStore.token and then invalidate React Query
// caches so list/detail views refetch under the new scope.
export type FactoryRecord = {
  id: number;
  code: string;
  name: string;
  short_name: string | null;
  address?: string | null;
  contact_phone?: string | null;
  email?: string | null;
  legal_name?: string | null;
  legal_name_en?: string | null;
  tax_id?: string | null;
  bank_name?: string | null;
  bank_account?: string | null;
  status?: number;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
};

export const factoryApi = {
  /** Public active factories for unauthenticated self-registration. */
  listPublic: () =>
    apiGet<Array<{ id: number; code: string; name: string; short_name: string | null }>>(
      '/factories/public',
    ),
  list: () =>
    apiGet<{
      factories: Array<{ id: number; code: string; name: string; short_name: string | null }>;
      current: number;
      primary: number;
      hasAllFactoriesAccess: boolean;
    }>('/factories'),
  /**
   * All active factories, for admin/production dispatch UIs. The standard
   * list() is narrowed by access; this one is not.
   */
  listAll: () =>
    apiGet<Array<{ id: number; code: string; name: string; short_name: string | null }>>(
      '/factories/all',
    ),
  /** Admin-only factory management list (includes disabled rows). */
  listForManage: () => apiGet<FactoryRecord[]>('/factories/manage'),
  create: (data: {
    code: string;
    name: string;
    short_name?: string;
    address?: string;
    contact_phone?: string;
    email?: string;
    legal_name?: string;
    legal_name_en?: string;
    tax_id?: string;
    bank_name?: string;
    bank_account?: string;
    sort_order?: number;
  }) => apiPost<{ id: number; code: string; name: string }>('/factories', data),
  update: (
    id: number,
    data: Partial<{
      code: string;
      name: string;
      short_name: string;
      address: string;
      contact_phone: string;
      email: string;
      legal_name: string;
      legal_name_en: string;
      tax_id: string;
      bank_name: string;
      bank_account: string;
      sort_order: number;
      status: number;
    }>,
  ) => apiPut<Ack>(`/factories/${id}`, data),
  switchFactory: (factory_id: number) =>
    apiPost<{ token: string; current_factory_id: number }>('/auth/switch-factory', { factory_id }),
};

export const portalApi = {
  getContent: () => apiGet<any>('/portal/content'),
  listContents: (params?: any) => apiGet<any[]>('/portal/admin/contents', { params }),
  createContent: (data: any) => apiPost<IdAck>('/portal/admin/contents', data),
  updateContent: (id: number, data: any) => apiPut<Ack>(`/portal/admin/contents/${id}`, data),
  deleteContent: (id: number) => apiDelete<Ack>(`/portal/admin/contents/${id}`),
  createLead: (data: any) => apiPost<IdAck>('/portal/leads', data),
  getLeads: () => apiGet<any[]>('/portal/leads'),
  updateLead: (id: number, data: any) => apiPut<Ack>(`/portal/leads/${id}`, data),
};
